import { useEffect, useRef } from 'react';

// Abort + monotonic-seq guard around a mount/deps fetch (generalizes Users'
// `requestSeq`). Callers should memoize callbacks with useCallback when they
// want to avoid rerunning the fetch on each render.
export function useFetchOnMount<T>({
  enabled = true,
  fetcher,
  onData,
  onError,
}: {
  enabled?: boolean;
  fetcher: (signal: AbortSignal) => Promise<T>;
  onData: (data: T) => void;
  onError: (error: unknown) => void;
}) {
  const seqRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const seq = ++seqRef.current;

    void fetcher(controller.signal)
      .then((data) => {
        if (seq === seqRef.current && !controller.signal.aborted) onData(data);
      })
      .catch((error) => {
        if (!controller.signal.aborted) onError(error);
      });

    return () => controller.abort();
  }, [enabled, fetcher, onData, onError]);
}
